/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { accessibleEntityColorsWithWhiteBackground } from 'src/core/themes/themeBuilder';

/* eslint-disable no-bitwise */
/**
 * Normalizes the given value to make sure it doesn't
 * exceed the normal color values limit.
 *
 * @param value The value to normalize.
 * @returns A normalized value based on the given one.
 */
const normalizeColorValue = (value: number) => {
    if (value > 255) {
        return 255;
    }

    if (value < 0) {
        return 0;
    }

    return value;
};

const decimalToHex = (value: number): string => {
    if (value > 255) {
        return 'FF';
    }
    if (value < 0) {
        return '00';
    }
    return value
        .toString(16)
        .padStart(2, '0')
        .toUpperCase();
};

const rgbToHex = (red: number, green: number, blue: number) => `#${decimalToHex(red)}${decimalToHex(green)}${decimalToHex(blue)}`;

/**
 * Darkens or lightens the given color with the percentage
 * given in the function. Positive values of the percentage
 * lighten the color while negative values darken it.
 *
 * @param color The base color to darken or lighten.
 * @param percentage The percentage by which the color should
 * be either darkened or lightened. To lighten, use a positive
 * value, and vice versa for darkening.
 * @returns The lightened/darkened color.
 */
export const lightenOrDarkenColor = (color: string, percentage: number): string => {
    let usePound = false;

    if (color[0] === '#') {
        color = color.slice(1);
        usePound = true;
    }

    const colorIntegerNumber = parseInt(color, 16);

    let redValue = (colorIntegerNumber >> 16) + percentage;
    redValue = normalizeColorValue(redValue);

    let blueValue = ((colorIntegerNumber >> 8) & 0x00ff) + percentage;
    blueValue = normalizeColorValue(blueValue);

    let greenValue = (colorIntegerNumber & 0x0000ff) + percentage;
    greenValue = normalizeColorValue(greenValue);

    return `${usePound ? '#' : ''}${(greenValue | (blueValue << 8) | (redValue << 16)).toString(16)}`;
};

export const generateColorFromString = (s: string): string => {
    let hash = 0;
    const rgb = [0, 0, 0];

    if (s.length === 0) {
        return rgbToHex(0, 0, 0);
    }

    for (let i = 0; i < s.length; i++) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
        hash &= hash;
    }

    for (let i = s.length - 1; i >= 0; i--) {
        hash = s.charCodeAt(i) + ((hash << 5) - hash);
        hash &= hash;
    }

    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 255;
        rgb[i] = value;
    }

    return rgbToHex(rgb[0], rgb[1], rgb[2]);
};

export const generateEntityColorFromString = (s: string): string => {
    const newStr = s ? s.toUpperCase() : s;

    let hash = 0;

    for (let i = 0; i < newStr?.length; i++) {
        hash = newStr.charCodeAt(i) + ((hash << 5) - hash);

        hash &= hash;
    }

    hash =
        ((hash % accessibleEntityColorsWithWhiteBackground.length) + accessibleEntityColorsWithWhiteBackground.length) %
        accessibleEntityColorsWithWhiteBackground.length;

    return accessibleEntityColorsWithWhiteBackground[hash];
};
